import type { ReactNode } from 'react';

import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Alert.module.css';

export type AlertTone = 'danger' | 'warning' | 'info' | 'success';

const ICONS = {
  danger: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: Check,
} as const;

export type AlertProps = {
  tone?: AlertTone;
  children: ReactNode;
  className?: string | undefined;
};

/**
 * An inline message attached to the thing it is about.
 *
 * Errors that belong to a form live here rather than in a toast: a toast that
 * disappears while the user is still reading the form is no help at all.
 *
 * `role="alert"` for failures so they are announced when they appear; the others are
 * ordinary content the user will reach on their own.
 */
export function Alert({ tone = 'info', children, className }: AlertProps) {
  const Icon = ICONS[tone];
  return (
    <div
      className={cx(styles.alert, styles[tone], className)}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      <span className={styles.icon} aria-hidden="true">
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );
}
